import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, ShieldOff, Check, AlertCircle, KeyRound, ShieldAlert } from 'lucide-react';
import { setupMfaThunk, verifyMfaSetupThunk, disableMfaThunk } from '../../../redux/settingsPages/security/securityActions';
import { clearMfaSetupData, clearSecurityErrors } from '../../../redux/settingsPages/security/securitySlice';
import { loadUserProfile } from '../../../redux/authentication/utils/core/authslice';

const CODE_LENGTH = 6;

const SecuritySettingsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.auth?.user);
  const {
    setupData, loading, error,
    verifying, verifyError,
    disabling, disableError,
  } = useSelector((state) => state.security);

  const [setupCode, setSetupCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState('');

  const mfaEnabled = !!user?.mfa_enabled;
  // Set by LoginPage's redirect when MFA_ENFORCEMENT_MODE requires this
  // account to set up MFA -- auto-starts the QR flow instead of making the
  // user find and click "Enable Two-Factor Authentication" themselves.
  const requiredByPolicy = !!location.state?.autoStartMfaSetup;

  // PrivateRoute already guarantees `user` is populated (from the login
  // flow) by the time this page can even mount -- dispatching
  // unconditionally here created a mount/unmount loop: this dispatch sets
  // loading=true, which makes PrivateRoute unmount its children (this page)
  // until the fetch resolves, which then re-mounts the page and fires the
  // same unconditional dispatch again, forever. Only fetch if we somehow
  // don't have a user yet.
  useEffect(() => {
    if (!user) dispatch(loadUserProfile());
  }, [dispatch, user]);

  useEffect(() => {
    if (requiredByPolicy && !mfaEnabled && !setupData && !loading) {
      dispatch(setupMfaThunk());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredByPolicy]);

  const handleStartSetup = () => {
    dispatch(clearSecurityErrors());
    dispatch(setupMfaThunk());
  };

  const handleCancelSetup = () => {
    dispatch(clearMfaSetupData());
    setSetupCode('');
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    try {
      await dispatch(verifyMfaSetupThunk(setupCode)).unwrap();
      setSetupCode('');
      setShowSuccess(t('settings.security.successEnabled'));
      dispatch(loadUserProfile());
      setTimeout(() => setShowSuccess(''), 4000);
    } catch {
      // verifyError already surfaced via redux state
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    try {
      await dispatch(disableMfaThunk(disableCode)).unwrap();
      setDisableCode('');
      setShowDisableForm(false);
      setShowSuccess(t('settings.security.successDisabled'));
      dispatch(loadUserProfile());
      setTimeout(() => setShowSuccess(''), 4000);
    } catch {
      // disableError already surfaced via redux state
    }
  };

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings.security.pageTitle')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('settings.security.pageSubtitle')}</p>
      </div>

      <div className="max-w-3xl space-y-6">
        {requiredByPolicy && !mfaEnabled && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-600/30 rounded-lg p-4 flex items-center">
            <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 mr-3 flex-shrink-0" />
            <p className="text-amber-700 dark:text-amber-400 text-sm">
              {t('settings.security.policyRequiredNotice')}
            </p>
          </div>
        )}

        {showSuccess && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600/30 rounded-lg p-4 flex items-center">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
            <p className="text-green-700 dark:text-green-400">{showSuccess}</p>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {mfaEnabled ? (
                <ShieldCheck className="w-6 h-6 text-green-500" />
              ) : (
                <ShieldOff className="w-6 h-6 text-gray-400" />
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings.security.mfaTitle')}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {mfaEnabled
                    ? t('settings.security.mfaEnabledDescription')
                    : t('settings.security.mfaDisabledDescription')}
                </p>
              </div>
            </div>
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
              mfaEnabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {mfaEnabled ? t('settings.security.statusOn') : t('settings.security.statusOff')}
            </span>
          </div>

          <div className="p-6">
            {/* Enabled, not currently disabling */}
            {mfaEnabled && !showDisableForm && (
              <button
                onClick={() => setShowDisableForm(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                {t('settings.security.disableButton')}
              </button>
            )}

            {/* Enabled, disabling flow */}
            {mfaEnabled && showDisableForm && (
              <form onSubmit={handleDisable} className="space-y-4 max-w-sm">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings.security.disableConfirmPrompt')}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={CODE_LENGTH}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-xl tracking-[0.4em] border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-red-500 focus:border-red-500"
                />
                {disableError && (
                  <div className="flex items-center text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    {disableError}
                  </div>
                )}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => { setShowDisableForm(false); setDisableCode(''); dispatch(clearSecurityErrors()); }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('settings.security.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={disabling || disableCode.length !== CODE_LENGTH}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50"
                  >
                    {disabling ? t('settings.security.disabling') : t('settings.security.confirmDisable')}
                  </button>
                </div>
              </form>
            )}

            {/* Not enabled, no setup in progress */}
            {!mfaEnabled && !setupData && (
              <button
                onClick={handleStartSetup}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 flex items-center"
              >
                {loading ? t('settings.security.startingSetup') : t('settings.security.enableButton')}
              </button>
            )}
            {!mfaEnabled && error && !setupData && (
              <div className="mt-3 flex items-center text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Setup in progress: QR + verify code */}
            {!mfaEnabled && setupData && (
              <div className="space-y-5 max-w-sm">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {t('settings.security.scanQrInstructions')}
                  </p>
                  <div className="bg-white p-4 rounded-lg inline-block border border-gray-200">
                    <img src={setupData.qr_code} alt={t('settings.security.qrAltText')} className="w-48 h-48" />
                  </div>
                </div>

                <div className="flex items-start space-x-2 text-xs text-gray-500 dark:text-gray-400">
                  <KeyRound className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{t('settings.security.manualKeyPrompt')} <code className="font-mono">{setupData.secret}</code></span>
                </div>

                <form onSubmit={handleVerifySetup} className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('settings.security.enterCodeLabel')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={CODE_LENGTH}
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                    placeholder="000000"
                    className="w-full px-4 py-3 text-center text-xl tracking-[0.4em] border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  {verifyError && (
                    <div className="flex items-center text-sm text-red-600 dark:text-red-400">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      {verifyError}
                    </div>
                  )}
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleCancelSetup}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      {t('settings.security.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={verifying || setupCode.length !== CODE_LENGTH}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                      {verifying ? t('settings.security.verifying') : t('settings.security.verifyAndEnable')}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettingsPage;
